import React, { FC, useContext, useEffect, useState } from "react";
import { withStyles, createStyles, WithStyles, Theme } from "@material-ui/core/styles";

import TextField from "@material-ui/core/TextField";
import Chip from "@material-ui/core/Chip";
import LinearProgress from "@material-ui/core/LinearProgress";

import { arrivals } from "../../lib/api/arrival";
import { defects } from "../../lib/api/defects";
import { UserContext } from "../../lib/context/UserContext";

const styles = (theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(1),
      height: "100%",
      width: "100%",
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: theme.spacing(2),
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
  });

type ArrivalsDetailCommentsProps = {
  arrival_id: Number;
  handleChange: (property, value) => void;
} & WithStyles<typeof styles>;

const ArrivalsDetailCommentsUnstyled: FC<ArrivalsDetailCommentsProps> = (props) => {
  const { classes, arrival_id, handleChange } = props;

  const [comment, setComment] = useState("");
  const [isDirty, setIsDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [defectsSelected, setDefectsSelected] = useState([]);

  useEffect(() => {
    loadData();
  }, [arrival_id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const result = await arrivals.single(arrival_id);
      if (result) {
        setComment(result.comments);
        setDefectsSelected(result.defects.split(","));
      }
    } catch (error) {}
    setLoading(false);
  };

  if (loading) return <LinearProgress color="secondary" />;

  return (
    <div className={classes.root}>
      <QualityScoreTable />
      <ArrivalDefects isDirty={isDirty} defectsSelected={defectsSelected} setDefectsSelected={setDefectsSelected} toggleDirty={setIsDirty} handleChange={handleChange} />
      <ArrivalComments comment={comment} setComment={setComment} isDirty={isDirty} toggleDirty={setIsDirty} handleChange={handleChange} />
    </div>
  );
};

export default withStyles(styles)(ArrivalsDetailCommentsUnstyled);

type ArrivalCommentsProps = {
  isDirty: boolean;
  comment: string;
  setComment(comment: string): void;
  toggleDirty(isDirty: boolean): void;
  handleChange(property: string, value: any): void;
};

const ArrivalComments: FC<ArrivalCommentsProps> = ({ comment, isDirty, toggleDirty, setComment, handleChange }) => {
  const { user } = useContext(UserContext);

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <p style={{ fontWeight: "bold", lineHeight: 0, margin: 0 }}>COMMENTS</p>
      <TextField
        disabled={!user.edit_arrival_comments}
        multiline
        fullWidth
        rows={5}
        id="outlined-textarea"
        label="Additional Comments"
        placeholder="Comment"
        margin="normal"
        variant="outlined"
        value={comment || ""}
        style={{ height: "100%" }}
        InputProps={{
          style: { height: "190px", alignItems: "flex-start" },
        }}
        onChange={(e) => {
          setComment(e.target.value);
          toggleDirty(true);
        }}
        onBlur={() => {
          if (isDirty) {
            handleChange("comments", comment);
            toggleDirty(false); // Reset isDirty after handling the change
          }
        }}
      />
    </div>
  );
};

type ArrivalDefectsProps = {
  isDirty: boolean;
  defectsSelected: string[];
  toggleDirty(isDirty: boolean): void;
  handleChange(property: string, value: any): void;
  setDefectsSelected(defectsSelected: string[]): void;
};

const ArrivalDefects: FC<ArrivalDefectsProps> = ({ defectsSelected, isDirty, toggleDirty, handleChange, setDefectsSelected }) => {
  const [defectData, setDefectData] = useState([]);

  const loadData = async () => {
    const data = await defects.all();
    if (data && data.length > 0) {
      const defectsSorted = data.sort((a, b) => a.defect.localeCompare(b.defect));
      setDefectData(defectsSorted);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <p style={{ fontWeight: "bold", lineHeight: 0, margin: 0 }}>DEFECTS</p>
      <TextField
        select
        fullWidth
        label="Select"
        id="defects"
        value={[]}
        variant="outlined"
        margin="normal"
        style={{
          width: "100%",
          marginBottom: "16px",
        }}
        SelectProps={{
          native: false,
          multiple: true,
        }}
        onChange={(e) => {
          const newArray = [...defectsSelected, ...(Array.isArray(e.target.value) ? e.target.value : [e.target.value])];
          const uniqueArray = Array.from(new Set(newArray));
          setDefectsSelected(uniqueArray);
          handleChange("defects", uniqueArray.sort((a, b) => a.localeCompare(b)).join(","));
          toggleDirty(false);
        }}
      >
        {defectData
          .filter((defectItem) => !defectsSelected.includes(defectItem.defect) && defectItem.active)
          .map((item, index) => (
            <option key={index} value={item.defect}>
              {item.defect}
            </option>
          ))}
      </TextField>
      <div style={{ display: "flex", flexDirection: "column", flexWrap: "wrap", gap: "5px", alignItems: "flex-start" }}>
        {defectsSelected
          .filter((item) => item.length > 0)
          .map((item, index) => (
            <Chip
              key={index}
              label={item}
              color="primary"
              onDelete={() => {
                const newArray = defectsSelected.filter((defect) => defect !== item);
                setDefectsSelected(newArray);
                handleChange("defects", newArray.join(","));
                toggleDirty(true);
              }}
            />
          ))}
      </div>
    </div>
  );
};

export const QualityScoreTable = () => {
  const qualityScoreTableRow = (valueCol1: string, valueCol2: string, valueCol3: string, color: string) => {
    const commentFontWeight = valueCol3.toLowerCase() == "comment" ? "bold" : "normal";
    return (
      <tr>
        <td style={{ width: "60px", border: "1px solid black", padding: "3px", backgroundColor: "lightgray", fontWeight: "bold" }}>{valueCol1}</td>
        <td style={{ width: "60px", border: "1px solid black", padding: "3px", backgroundColor: "lightgray", fontWeight: "bold" }}>{valueCol2}</td>
        <td style={{ width: "200px", border: "1px solid black", padding: "3px", backgroundColor: color, fontWeight: commentFontWeight }}>{valueCol3}</td>
      </tr>
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <p style={{ fontWeight: "bold", lineHeight: 0, margin: 0 }}>QUALITY SCORE</p>
      <table style={{ marginTop: "16px" }}>
        <tbody>
          {qualityScoreTableRow("score", "colour", "comment", "lightgray")}
          {qualityScoreTableRow("1", "red", "total loss", "red")}
          {qualityScoreTableRow("2", "orange", "definite claim", "orange")}
          {qualityScoreTableRow("3", "green", "accepted with deviations - possible claim", "green")}
          {qualityScoreTableRow("4", "white", "good arrival", "white")}
        </tbody>
      </table>
    </div>
  );
};
